import { DeleteOutlined, SaveOutlined } from '@ant-design/icons'
import { PageHeader } from '@ant-design/pro-layout'
import { type ApolloError, useMutation, useSuspenseQuery } from '@apollo/client'
import {
  CreateDimensionGroupDocument,
  type DimensionGroupFieldsFragment,
  FetchDimensionGroupsDocument,
  UpdateDimensionGroupDocument,
} from '@salescore/client-api'
import { Posthog, POSTHOG_EVENTS } from '@salescore/client-base'
import { ErrorContext, getOrganizationIdFromPath, useCanForFeatures, useMe } from '@salescore/client-common'
import type { DimensionGroup } from '@salescore/core'
import { canUseOrganizationLicenseForModel } from '@salescore/features'
import { Alert, Button, Col, Form, Input, List, message, Row, Select, Space } from 'antd'
import { useForm } from 'antd/es/form/Form'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { type ReactElement, useContext, useState } from 'react'

import { PropertiesList } from './PropertiesList'
import { PropertyCascader } from './PropertyCascader'

type FormValue = Omit<DimensionGroup, 'id'>

// eslint-disable-next-line sonarjs/prefer-read-only-props
export function UpsertDimensionGroupForm({
  dimensionGroup,
  withoutGoalFormItem,
  onAfterFinish,
}: {
  dimensionGroup?: DimensionGroupFieldsFragment
  withoutGoalFormItem?: boolean
  onAfterFinish: (x: DimensionGroupFieldsFragment) => void
}): ReactElement {
  const me = useMe()
  const { canManageDimensionGroup } = useCanForFeatures()
  const { data } = useSuspenseQuery(FetchDimensionGroupsDocument, {
    variables: { organizationId: getOrganizationIdFromPath() },
  })
  const { models, goalDimensions } = data
  const [form] = useForm<FormValue>()
  const [updateDimensionGroupMutation, { loading: updateLoading }] = useMutation(UpdateDimensionGroupDocument)
  const [createDimensionGroupMutation, { loading: createLoading }] = useMutation(CreateDimensionGroupDocument)
  const errorContext = useContext(ErrorContext)
  const [properties, setProperties] = useState(dimensionGroup?.properties ?? [])
  const removeModelProperty = (index: number): void => {
    setProperties((xs) => {
      const modelProperties = xs.filter((x) => x.type === 'modelProperty')
      const goalDimensions = xs.filter((x) => x.type === 'goalDimension')
      return [...modelProperties.slice(0, index), ...modelProperties.slice(index + 1), ...goalDimensions]
    })
  }
  const filteredModels = models.filter((x) => canUseOrganizationLicenseForModel(me.myUser, x.name))

  const onSaved = (saved: DimensionGroupFieldsFragment, toastMessage: string): void => {
    onAfterFinish(saved)
    void message.info(toastMessage)
    Posthog.track(POSTHOG_EVENTS.save_dimention_group, {
      organizationId: getOrganizationIdFromPath(),
      config: saved,
    })
  }

  const onError = (error: ApolloError): void => {
    message.error(t(`エラーが発生しました`))
    errorContext.throwErrors(error)
  }

  const onFinish = (values: FormValue): void => {
    if (dimensionGroup === undefined) {
      void createDimensionGroupMutation({
        variables: {
          organizationId: getOrganizationIdFromPath(),
          dimensionGroup: {
            label: values.label,
            properties,
          },
        },
        onCompleted(data) {
          const saved = data.createDimensionGroup
          onSaved(saved, t(`軸グループ設定を作成しました！`))
        },
        onError,
      })
    } else {
      void updateDimensionGroupMutation({
        variables: {
          organizationId: getOrganizationIdFromPath(),
          dimensionGroup: {
            id: dimensionGroup.id,
            label: values.label,
            properties,
          },
        },
        onCompleted(data) {
          const saved = data.updateDimensionGroup
          onSaved(saved, t(`軸グループ設定を更新しました！`))
        },
        onError,
      })
    }
  }

  const title = dimensionGroup === undefined ? t(`軸グループ設定を作成`) : t(`軸グループ設定を編集`)

  if (!canManageDimensionGroup) {
    return (
      <PageHeader title={title}>
        <Alert type="warning" message={t(`権限がありません`)} />
      </PageHeader>
    )
  }

  return (
    <PageHeader title={title}>
      <Form<FormValue> form={form} onFinish={onFinish} layout="vertical" initialValues={dimensionGroup}>
        <Form.Item
          name="label"
          label={t(`軸グループ設定の名前`)}
          // tooltip={'「事業計画」「チーム目標」など'}
          rules={[{ required: true, message: t(`軸グループ設定の名前を入力してください`) }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="propertiesForModel" label={t(`グループ化したいプロパティを選択(複数選択)`)} required>
          {/* 新規に選べるのは license を持つサービスのモデルだけ */}
          <PropertyCascader
            models={filteredModels}
            className="mb-4"
            onChange={(x) => {
              // 既に選択済みのpropertyが選択されたら削除する
              if (
                properties.some(
                  (y) => y.type === 'modelProperty' && y.propertyName === x.propertyName && y.modelName === x.modelName,
                )
              ) {
                setProperties((xs) =>
                  xs.filter(
                    (y) =>
                      !(y.type === 'modelProperty' && y.propertyName === x.propertyName && y.modelName === x.modelName),
                  ),
                )
              } else {
                setProperties((xs) => [
                  ...xs,
                  {
                    type: 'modelProperty',
                    modelName: x.modelName,
                    propertyName: x.propertyName,
                  },
                ])
              }
            }}
          />
          {/* 選択済みプロパティのラベル解決に使うため license で絞らない。絞ると解約済みサービスのプロパティが内部名で表示される */}
          <PropertiesList
            models={models}
            propertyNames={properties.map((x) => (x.type === 'modelProperty' ? x : undefined)).filter(isNotNullish)}
            onDelete={removeModelProperty}
          />
        </Form.Item>
        {withoutGoalFormItem !== true && (
          <Form.Item name="propertiesForGoal" label={t(`グループ化したい目標軸を選択(複数選択)`)}>
            <Select
              options={goalDimensions.map((x) => ({
                label: <span className="whitespace-normal break-words">{x.label}</span>,
                value: x.id,
              }))}
              value={undefined}
              className="mb-4"
              onChange={(goalDimensionId: string) => {
                setProperties((xs) => [
                  ...xs,
                  {
                    type: 'goalDimension',
                    goalDimensionId,
                  },
                ])
              }}
            />
            <List
              dataSource={properties.map((x) => (x.type === 'goalDimension' ? x : undefined)).filter(isNotNullish)}
              bordered
              renderItem={(item) => {
                const goalDimension = goalDimensions.find((x) => x.id === item.goalDimensionId)
                return (
                  <List.Item
                    actions={[
                      <Button
                        key={1}
                        onClick={() => {
                          setProperties((xs) =>
                            xs.filter(
                              (x) => !(x.type === 'goalDimension' && x.goalDimensionId === item.goalDimensionId),
                            ),
                          )
                        }}
                        type="text"
                        icon={<DeleteOutlined />}
                      />,
                    ]}
                  >
                    {goalDimension?.label ?? item.goalDimensionId}
                  </List.Item>
                )
              }}
            />
          </Form.Item>
        )}
        <Form.Item>
          <Space className="mt-16 w-full flex-row-reverse">
            <Button icon={<SaveOutlined />} type="primary" loading={createLoading || updateLoading} htmlType="submit">
              {t(`保存`)}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </PageHeader>
  )
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
function PropertyFormItem({ property }: { property: Partial<FormValue['properties'][0]> }): ReactElement {
  return (
    <Row>
      <Col span={8}>
        <Form.Item>
          <Select
            value={property.type}
            options={[
              {
                value: 'modelProperty',
                label: t(`プロパティ`),
              },
              {
                value: 'goalDimension',
                label: t(`目標軸`),
              },
            ]}
          />
        </Form.Item>
      </Col>
      {property.type === 'modelProperty' && (
        <>
          <Col span={8}>
            <Form.Item>
              {/* PropertyPicker */}
              <Select />
            </Form.Item>
          </Col>
        </>
      )}
      {property.type === 'goalDimension' && (
        <>
          <Col span={8}>
            <Form.Item>{/* goalDimensionのpicker */}</Form.Item>
          </Col>
        </>
      )}
    </Row>
  )
}
